import logger from '../../lib/logger';
import React, { useState } from 'react';
import { Plus, Check, XCircle, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const DONATION_CATEGORIES = ["General", "Madrassah", "Youth Programs", "Building Fund", "Community Services", "Sadaqah", "Khums", "Zakat", "Other"];

const DonationsTab = ({ donations, loadingData, token, onRefresh, onDashboardRefresh, apiBaseUrl }) => {
  const [showAddDonation, setShowAddDonation] = useState(false);
  const [newDonation, setNewDonation] = useState({ name: '', email: '', amount: '', category: 'General', payment_method: 'etransfer', is_anonymous: false });

  const addDonation = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/donations`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newDonation, amount: parseFloat(newDonation.amount) })
      });
      if (response.ok) {
        setNewDonation({ name: '', email: '', amount: '', category: 'General', payment_method: 'etransfer', is_anonymous: false });
        setShowAddDonation(false);
        onRefresh();
        onDashboardRefresh();
      }
    } catch (err) { logger.error('Failed to add donation:', err); }
  };

  const updateDonationStatus = async (id, status) => {
    try { 
      await fetch(`${apiBaseUrl}/api/admin/donations/${id}/status?status=${status}`, { 
        method: 'PUT', 
        headers: { 'Authorization': `Bearer ${token}` } 
      }); 
      onRefresh(); 
      onDashboardRefresh(); 
    }
    catch (err) { logger.error('Failed to update:', err); }
  };

  const deleteDonation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this donation record?')) return;
    try {
      await fetch(`${apiBaseUrl}/api/admin/donations/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      onRefresh();
      onDashboardRefresh();
    } catch (err) { logger.error('Failed to delete donation:', err); }
  };

  return (
    <div className="bg-white shadow-lg">
      <div className="p-6 border-b flex justify-between items-center">
        <div>
          <h2 className="text-xl text-[#1a2f23]">Donations</h2>
          <p className="text-sm text-[#5a6d62]">Track and manage donation records</p>
        </div>
        <Button onClick={() => setShowAddDonation(true)} className="btn-primary"><Plus className="w-4 h-4" /> Record Donation</Button>
      </div>
      
      {showAddDonation && (
        <div className="p-6 bg-[#f8f6f1] border-b">
          <h3 className="text-lg text-[#1a2f23] mb-4">Record New Donation</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm text-[#5a6d62]">Donor Name</Label>
              <Input value={newDonation.name} onChange={(e) => setNewDonation({...newDonation, name: e.target.value})} className="mt-1" placeholder="Leave empty for anonymous" disabled={newDonation.is_anonymous} />
            </div>
            <div>
              <Label className="text-sm text-[#5a6d62]">Email</Label>
              <Input type="email" value={newDonation.email} onChange={(e) => setNewDonation({...newDonation, email: e.target.value})} className="mt-1" placeholder="donor@email.com" />
            </div>
            <div>
              <Label className="text-sm text-[#5a6d62]">Amount ($) *</Label>
              <Input type="number" min="0" step="0.01" value={newDonation.amount} onChange={(e) => setNewDonation({...newDonation, amount: e.target.value})} className="mt-1" placeholder="0.00" />
            </div>
            <div>
              <Label className="text-sm text-[#5a6d62]">Category *</Label>
              <Select value={newDonation.category} onValueChange={(v) => setNewDonation({...newDonation, category: v})}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{DONATION_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-[#5a6d62]">Payment Method *</Label>
              <Select value={newDonation.payment_method} onValueChange={(v) => setNewDonation({...newDonation, payment_method: v})}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="etransfer">E-Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="square">Square/Card</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={newDonation.is_anonymous} onChange={(e) => setNewDonation({...newDonation, is_anonymous: e.target.checked, name: e.target.checked ? '' : newDonation.name})} className="w-4 h-4 rounded border-gray-300" />
                <span className="text-sm text-[#5a6d62]">Anonymous Donation</span>
              </label>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={addDonation} className="bg-green-600 hover:bg-green-700 text-white" disabled={!newDonation.amount || !newDonation.category}><Check className="w-4 h-4" /> Save Donation</Button>
            <Button onClick={() => setShowAddDonation(false)} variant="outline">Cancel</Button>
          </div>
        </div>
      )}
      
      {loadingData ? <div className="p-8 text-center"><Loader2 className="w-8 h-8 text-[#c5a572] animate-spin mx-auto" /></div> : donations.length === 0 ? <div className="p-8 text-center text-[#5a6d62]">No donations yet</div> : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#f8f6f1]"><tr><th className="px-6 py-3 text-left text-xs font-medium text-[#5a6d62] uppercase">Donor</th><th className="px-6 py-3 text-left text-xs font-medium text-[#5a6d62] uppercase">Amount</th><th className="px-6 py-3 text-left text-xs font-medium text-[#5a6d62] uppercase">Category</th><th className="px-6 py-3 text-left text-xs font-medium text-[#5a6d62] uppercase">Method</th><th className="px-6 py-3 text-left text-xs font-medium text-[#5a6d62] uppercase">Status</th><th className="px-6 py-3 text-left text-xs font-medium text-[#5a6d62] uppercase">Actions</th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {donations.map(d => (
                <tr key={d.id}>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-[#1a2f23]">{d.is_anonymous ? 'Anonymous' : (d.name || '-')}</div>
                    <div className="text-xs text-[#5a6d62]">{d.email || '-'}</div>
                    <div className="text-xs text-[#5a6d62]/70">{new Date(d.created_at).toLocaleDateString()}</div>
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-green-600">${d.amount.toFixed(2)}</td>
                  <td className="px-6 py-4"><span className="text-sm bg-[#c5a572]/20 text-[#8a7045] px-2 py-1 rounded">{d.category}</span></td>
                  <td className="px-6 py-4 text-sm text-[#5a6d62] capitalize">{d.payment_method}</td>
                  <td className="px-6 py-4"><span className={`text-xs px-2 py-1 rounded ${d.status === 'completed' ? 'bg-green-100 text-green-800' : d.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>{d.status}</span></td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      {d.status === 'pending' && (
                        <>
                          <button onClick={() => updateDonationStatus(d.id, 'completed')} className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700" title="Mark as Completed"><Check className="w-3 h-3" /></button>
                          <button onClick={() => updateDonationStatus(d.id, 'failed')} className="text-xs bg-gray-400 text-white px-2 py-1 rounded hover:bg-gray-500" title="Mark as Failed"><XCircle className="w-3 h-3" /></button>
                        </>
                      )}
                      <button onClick={() => deleteDonation(d.id)} className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200" title="Delete"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DonationsTab;
